/**
 * Schedule olusturma / duzenleme modal'i.
 *
 * Alanlar:
 *  - Ad (Name) — opsiyonel, kullanici dostu etiket
 *  - Aktif/Pasif (IsActive)
 *  - Rapor Tipi + Cron Expression (CronExpressionPicker icinde birlikte secilir)
 *  - Subeler (Multi-select; bos = tum subeler)
 *  - Alicilar (Recipients) + CC (chip input)
 *
 * Backend: ReportSchedulesController create/update
 */

import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useAuth } from '../../../../contexts/AuthContext';
import type {
  ReportKind,
  ReportSchedule,
  ReportScheduleUpsertRequest,
  ReportType,
} from '../types';
import { REPORT_KIND_OPTIONS } from '../types';
import CronExpressionPicker from './CronExpressionPicker';
import { createSchedule, updateSchedule } from '../services/mailReportsApi';
import { humanizeCron, inferCronCadence } from '../utils/cronHumanizer';

const TIMEZONE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'Turkey Standard Time', label: 'Türkiye (UTC+3)' },
  { id: 'GMT Standard Time', label: 'Londra (UTC+0/+1)' },
  { id: 'W. Europe Standard Time', label: 'Berlin / Paris (UTC+1/+2)' },
  { id: 'Eastern European Standard Time', label: 'Atina (UTC+2/+3)' },
  { id: 'UTC', label: 'UTC (Koordineli Evrensel)' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (schedule: ReportSchedule) => void;
  schedule?: ReportSchedule | null;
}

function emailIsValid(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const ScheduleFormDialog: React.FC<Props> = ({ open, onClose, onSaved, schedule }) => {
  const { branches, userDetails } = useAuth();
  const isEdit = !!schedule;

  const [name, setName] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [cron, setCron] = React.useState('0 22 * * *');
  const [reportType, setReportType] = React.useState<ReportType>('daily');
  const [reportKind, setReportKind] = React.useState<ReportKind>('endOfDay');
  const [timeZoneId, setTimeZoneId] = React.useState('Turkey Standard Time');
  const [selectedBranchIds, setSelectedBranchIds] = React.useState<string[]>([]);
  const [recipients, setRecipients] = React.useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = React.useState<string[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Modal acildikca form'u doldur veya temizle
  React.useEffect(() => {
    if (!open) return;
    if (schedule) {
      setName(schedule.Name || '');
      setIsActive(schedule.IsActive);
      setCron(schedule.CronExpression);
      setReportType(schedule.ReportType);
      setReportKind((schedule.ReportKind as ReportKind) || 'endOfDay');
      setTimeZoneId(schedule.TimeZoneId || 'Turkey Standard Time');
      setSelectedBranchIds(schedule.BranchIds || []);
      setRecipients(schedule.Recipients || []);
      setCcRecipients(schedule.CcRecipients || []);
    } else {
      setName('');
      setIsActive(true);
      setCron('0 22 * * *');
      setReportType('daily');
      setReportKind('endOfDay');
      setTimeZoneId('Turkey Standard Time');
      setSelectedBranchIds([]);
      setRecipients([]);
      setCcRecipients([]);
    }
    setError(null);
  }, [open, schedule]);

  const handleSave = async () => {
    setError(null);

    // Validasyon
    if (recipients.length === 0) {
      setError('En az 1 alıcı email adresi gerekli.');
      return;
    }
    const invalidEmails = [...recipients, ...ccRecipients].filter((e) => !emailIsValid(e));
    if (invalidEmails.length > 0) {
      setError(`Geçersiz email: ${invalidEmails.join(', ')}`);
      return;
    }
    const cronParts = cron.trim().split(/\s+/);
    if (cronParts.length !== 5) {
      setError('Cron ifadesi 5 alan içermeli (örn: "0 22 * * *")');
      return;
    }

    const companyId = userDetails?.companyId || '';
    if (!companyId) {
      setError('Şirket ID bulunamadı (oturum açmış olmanız gerekli).');
      return;
    }

    const payload: ReportScheduleUpsertRequest = {
      CompanyId: companyId,
      BranchIds: selectedBranchIds,
      ReportType: reportType,
      ReportKind: reportKind,
      CronExpression: cron.trim(),
      TimeZoneId: timeZoneId,
      Recipients: recipients,
      CcRecipients: ccRecipients,
      Name: name.trim() || null,
      IsActive: isActive,
    };

    setSaving(true);
    try {
      const result = isEdit && schedule
        ? await updateSchedule(schedule.Id, payload)
        : await createSchedule(payload);
      onSaved(result);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.Message || err?.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // Sube secimi (chip ile)
  const branchOptions = (branches || []).map((b) => ({ id: b.id, name: b.name }));
  const selectedBranches = branchOptions.filter((b) => selectedBranchIds.includes(b.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Zamanlamayı Düzenle' : 'Yeni Zamanlama'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Ad (opsiyonel)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Günlük Akşam Raporu"
              size="small"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="success"
                />
              }
              label={isActive ? 'Aktif' : 'Pasif'}
            />
          </Stack>

          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <AssessmentIcon fontSize="small" sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle2">Rapor İçerik Tipi</Typography>
            </Stack>
            <FormControl fullWidth size="small">
              <Select
                value={reportKind}
                onChange={(e) => setReportKind(e.target.value as ReportKind)}
                renderValue={(val) => {
                  const opt = REPORT_KIND_OPTIONS.find((o) => o.kind === val);
                  return opt?.label || val;
                }}
              >
                {REPORT_KIND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.kind} value={opt.kind} disabled={!opt.available}>
                    <Stack sx={{ width: '100%' }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {opt.label}
                        </Typography>
                        {!opt.available && (
                          <Chip size="small" label="Yakında" color="default" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Mail içeriği olarak hangi raporun gönderileceği. Yeni raporlar eklendikçe
              bu liste otomatik genişleyecek.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Zamanlama
            </Typography>
            <CronExpressionPicker
              value={cron}
              reportType={reportType}
              onChange={(c, rt) => {
                setCron(c);
                setReportType(rt);
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Şubeler{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                (boş bırakırsanız tüm şubeler tek raporda birleşik gönderilir)
              </Typography>
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={branchOptions}
              getOptionLabel={(o) => o.name}
              value={selectedBranches}
              onChange={(_, newValue) => setSelectedBranchIds(newValue.map((b) => b.id))}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} placeholder="Şube seçin (çoğul)" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  return <Chip key={option.id} label={option.name} size="small" {...rest} />;
                })
              }
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Alıcılar (To) *
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={recipients}
              onChange={(_, newValue) =>
                setRecipients((newValue as string[]).map((s) => s.trim()).filter(Boolean))
              }
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  const valid = emailIsValid(option);
                  return (
                    <Chip
                      key={`${option}-${index}`}
                      label={option}
                      size="small"
                      color={valid ? 'default' : 'error'}
                      {...rest}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Email yazıp ENTER'a basın"
                  helperText="En az 1 alıcı gerekli"
                />
              )}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              CC (opsiyonel)
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={ccRecipients}
              onChange={(_, newValue) =>
                setCcRecipients((newValue as string[]).map((s) => s.trim()).filter(Boolean))
              }
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option, index) => {
                  const { key, ...rest } = getTagProps({ index });
                  const valid = emailIsValid(option);
                  return (
                    <Chip
                      key={`${option}-${index}`}
                      label={option}
                      size="small"
                      color={valid ? 'default' : 'error'}
                      {...rest}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="CC email yazip ENTER" />
              )}
            />
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel>Saat Dilimi</InputLabel>
            <Select
              label="Saat Dilimi"
              value={
                TIMEZONE_OPTIONS.find((o) => o.id === timeZoneId) ? timeZoneId : '__custom__'
              }
              onChange={(e) => {
                const v = e.target.value as string;
                if (v === '__custom__') return;
                setTimeZoneId(v);
              }}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.id} value={tz.id}>
                  {tz.label}
                </MenuItem>
              ))}
              {!TIMEZONE_OPTIONS.find((o) => o.id === timeZoneId) && (
                <MenuItem value="__custom__">{`Özel: ${timeZoneId}`}</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Cron-reportType uyumsuzluk uyarisi.
              Ornek: cron "0 22 * * *" (her gun) ama reportType="weekly" secildi —
              backend yine 7 gunluk pencere kullanir ama mail her gun gider. */}
          {(() => {
            const cadence = inferCronCadence(cron);
            if (cadence === 'custom' || cadence === reportType) return null;

            const cadenceLabel =
              cadence === 'daily' ? 'günlük' : cadence === 'weekly' ? 'haftalık' : 'aylık';
            const typeLabel =
              reportType === 'daily' ? 'günlük' : reportType === 'weekly' ? 'haftalık' : 'aylık';

            return (
              <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Cron tetiklenme sıklığı rapor tipi ile uyumsuz
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Cron her <strong>{cadenceLabel}</strong> tetiklenecek
                  ({humanizeCron(cron)}), ancak rapor tipi <strong>{typeLabel}</strong>.
                  Mail her tetiklendiğinde son <strong>
                    {reportType === 'weekly' ? '7 günlük' : reportType === 'monthly' ? '1 aylık' : '1 günlük'}
                  </strong> özet ile gidecek.
                </Typography>
              </Alert>
            );
          })()}

          {/* Insan dilinde ozet — kullaniciya teyit goster */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              background: 'linear-gradient(135deg,rgba(79,70,229,0.04) 0%,rgba(124,58,237,0.04) 100%)',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Özet
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <EmailIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.25 }} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {(REPORT_KIND_OPTIONS.find((o) => o.kind === reportKind)?.label) || reportKind}
                  {' • '}
                  {humanizeCron(cron)} ({reportType === 'daily' ? 'günlük' : reportType === 'weekly' ? 'haftalık' : 'aylık'})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedBranchIds.length === 0
                    ? 'Tüm şubeler tek raporda'
                    : `${selectedBranchIds.length} şube`}
                  {' • '}
                  {recipients.length} alıcı
                  {ccRecipients.length > 0 ? ` + ${ccRecipients.length} CC` : ''}
                  {' • '}
                  {(TIMEZONE_OPTIONS.find((o) => o.id === timeZoneId)?.label) || timeZoneId}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleFormDialog;
